import { useState } from "react";
import { Loader2, MessageSquareWarning } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { APP_VERSION } from "@shared/version";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { apiPost } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthContext";

import { mitAuswahlImBild } from "./auswahlFuersBild";

// ---------------------------------------------------------------------------
// FeedbackButton — G-14 «Problem melden» (Header, ueberall verfuegbar)
// ---------------------------------------------------------------------------
// Klick rendert die aktuelle Seite client-seitig zum Bild (DOM-zu-Canvas via
// modern-screenshot, KEIN getDisplayMedia-Berechtigungs-Popup), DANN oeffnet
// der Dialog — so ist der Dialog selbst nie im Screenshot. Vorschau mit
// Abwahl-Moeglichkeit (Screenshots koennen EK-/Kundendaten zeigen).
// modern-screenshot wird lazy importiert (~50 KB nur bei Klick).

// Base64-Deckel: Vercel-Body-Limit ist ~4.5 MB; der Server prueft 4 MB
// dekodiert. Erst Qualitaet senken, dann notfalls ohne Screenshot melden.
const SCREENSHOT_MAX_DATAURL_CHARS = 5_000_000;

async function captureScreenshot(): Promise<string | null> {
  try {
    const { domToJpeg } = await import("modern-screenshot");
    const background =
      getComputedStyle(document.documentElement).backgroundColor;
    const capture = (quality: number) =>
      domToJpeg(document.body, {
        quality,
        backgroundColor: background,
        // Retina-Faktor kappen — 1:1-Pixel reichen fuer die Diagnose und
        // halten die Datei klein.
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
      });
    // ZUSATZ-2: Auswahlfelder zeigen im Bild, was gewaehlt ist.
    let dataUrl = await mitAuswahlImBild(document, () => capture(0.8));
    if (dataUrl.length > SCREENSHOT_MAX_DATAURL_CHARS) {
      dataUrl = await mitAuswahlImBild(document, () => capture(0.5));
    }
    return dataUrl.length > SCREENSHOT_MAX_DATAURL_CHARS ? null : dataUrl;
  } catch {
    // Screenshot ist Beigabe — die Meldung selbst darf nie daran scheitern.
    return null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Flow-State fuer «Problem melden»: capture → Dialog. Als Hook, damit der
 * Mobile-Drawer den Flow AUSSERHALB des Sheets halten kann (der Drawer
 * unmountet beim Schliessen — der Dialog muss das ueberleben).
 */
export function useFeedbackFlow() {
  const [capturing, setCapturing] = useState(false);
  const [dialog, setDialog] = useState<{ screenshot: string | null } | null>(
    null,
  );

  async function start(opts?: { delayMs?: number }) {
    if (capturing) return;
    setCapturing(true);
    try {
      // Mobile: Drawer-Schliess-Animation abwarten, sonst ist er im Bild.
      if (opts?.delayMs) await sleep(opts.delayMs);
      const screenshot = await captureScreenshot();
      setDialog({ screenshot });
    } finally {
      setCapturing(false);
    }
  }

  const dialogNode = dialog ? (
    <FeedbackDialog
      screenshot={dialog.screenshot}
      onClose={() => setDialog(null)}
    />
  ) : null;

  return { capturing, start, dialogNode };
}

/** Desktop-TopNav: dezenter Icon-Button, self-contained. */
export function FeedbackButton() {
  const flow = useFeedbackFlow();
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => void flow.start()}
            disabled={flow.capturing}
            aria-label="Problem melden"
            data-testid="feedback-button"
            className="inline-flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground disabled:opacity-60"
          >
            {flow.capturing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <MessageSquareWarning className="size-4" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>Problem melden</TooltipContent>
      </Tooltip>
      {flow.dialogNode}
    </>
  );
}

/** Mobile-Drawer-Zeile: dumb Button — der Flow lebt im MobileTopNav. */
export function FeedbackMenuButton({
  capturing,
  onStart,
}: {
  readonly capturing: boolean;
  readonly onStart: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onStart}
      disabled={capturing}
      data-testid="feedback-button-mobile"
      className="inline-flex min-h-11 items-center gap-3 rounded-sm border border-border bg-surface px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground disabled:opacity-60"
    >
      {capturing ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <MessageSquareWarning className="size-4" />
      )}
      <span>Problem melden</span>
    </button>
  );
}

export function FeedbackDialog({
  screenshot,
  onClose,
}: {
  readonly screenshot: string | null;
  readonly onClose: () => void;
}) {
  const { user } = useAuth();
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);

  const route = `${location.pathname}${location.search}`;

  const submitMutation = useMutation({
    mutationFn: async () =>
      apiPost<{ ok: true; data: { id: string } }>("/api/feedback", {
        message: message.trim(),
        route,
        appVersion: APP_VERSION,
        viewport: `${String(window.innerWidth)}x${String(window.innerHeight)}`,
        userAgent: navigator.userAgent.slice(0, 400),
        screenshotDataUrl:
          screenshot && includeScreenshot ? screenshot : null,
      }),
    onSuccess: () => {
      toast.success("Meldung gesendet — danke!");
      onClose();
    },
    onError: (err) => {
      toast.error(
        `Meldung konnte nicht gesendet werden: ${err instanceof Error ? err.message : String(err)}`,
      );
    },
  });

  const canSubmit = message.trim().length > 0 && !submitMutation.isPending;

  // FB-E (Marvin 23.07, Daniel-Klaerung 23.07): Enter SENDET ab (Marvins
  // Erwartung). Das Feld bleibt mehrzeilig — eine neue Zeile macht Shift+Enter
  // (Chat-Konvention wie Slack/WhatsApp). IME-Komposition (isComposing) nicht
  // unterbrechen, sonst schluckt es Enter mitten in der Zeichen-Eingabe.
  const handleMessageKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSubmit) submitMutation.mutate();
    }
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(next) => {
        if (!next && !submitMutation.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Problem melden</DialogTitle>
          <DialogDescription>
            Beschreibe kurz, was nicht passt. Enter sendet ab, Shift+Enter macht
            eine neue Zeile. Seite und Version gehen automatisch mit an Daniel.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleMessageKeyDown}
            rows={4}
            autoFocus
            placeholder="Was passt nicht?"
            data-testid="feedback-message"
            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
          />

          {screenshot ? (
            <div className="flex flex-col gap-2">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(e) => setIncludeScreenshot(e.target.checked)}
                  data-testid="feedback-screenshot-toggle"
                  className="mt-0.5"
                />
                <span className="text-sm">
                  Screenshot mitsenden
                  <span className="block text-xs text-muted-foreground">
                    Abwählen, falls vertrauliche Daten sichtbar sind.
                  </span>
                </span>
              </label>
              <img
                src={screenshot}
                alt="Screenshot der aktuellen Seite"
                className={`max-h-48 w-full rounded-sm border border-border object-contain object-top ${includeScreenshot ? "" : "opacity-30 grayscale"}`}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Screenshot konnte nicht erstellt werden — die Meldung geht ohne
              Bild raus.
            </p>
          )}

          <p className="font-mono text-2xs text-muted-foreground">
            {route} · v{APP_VERSION}
            {user ? ` · ${user.fullName}` : ""}
          </p>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => submitMutation.mutate()}
              disabled={!canSubmit}
              data-testid="feedback-submit"
            >
              {submitMutation.isPending ? "Wird gesendet…" : "Melden"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
